/**
 * CRM docket financials — same SSOT as PackageOrderDetailsPremiumView payment card.
 */
import { resolveLdsFinancialState } from "../../../utils/orders/resolveLdsFinancialState";
import { resolveLdsBaggageBreakdown } from "../../../utils/orders/resolveLdsBaggageBreakdown";
import { resolveAbsoluteAgentHandlingFeeIls } from "../../../utils/resolveAbsoluteAgentHandlingFeeIls";
import { formatOrderDetailsIlsWithUsdSecondary } from "../../../utils/orders/orderDetailsAmountDisplay";
import {
  resolveLdsOrderAuthoritativeUsdMap,
  resolveLdsOrderLockedCurrencyRateMap,
} from "../../../utils/orders/resolveLdsOrderAuthoritativeUsd";
import {
  parseLdsOrderPayments,
  resolveLdsPaymentSlotCapturedStrict,
} from "../../../utils/orders/ldsSplitPaymentsSsot";
import { canViewHrCommission } from "../../../services/authService";

function ourprice(order) {
  const op = order?.ourprice || order?.ourPrice;
  return op && typeof op === "object" ? op : {};
}

function num(val) {
  if (val == null || val === "") return null;
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
}

function fmtIlsUsd(ilsAmount, authoritativeUsd, currencyRate, peers = {}) {
  return formatOrderDetailsIlsWithUsdSecondary({
    ilsAmount,
    authoritativeUsd,
    currencyRate,
    peerIls: peers.peerIls,
    peerUsd: peers.peerUsd,
  });
}

/**
 * Real baggage only — never treat agent handling fee / handleFee as "כבודה".
 */
function resolveCrmBaggageIls(order, agentFeeIls) {
  const baggage = resolveLdsBaggageBreakdown(order, { emitTrace: false });
  const hasSupplementItems = Array.isArray(baggage?.items) && baggage.items.length > 0;
  if (!hasSupplementItems) {
    // Fallback often mirrors handleFee wrongly — ignore for CRM
    return null;
  }
  const amount = num(baggage.totalBaggageAmountIls);
  if (amount == null || amount <= 0) return null;
  if (agentFeeIls != null && Math.abs(amount - Number(agentFeeIls)) < 0.51) {
    // Same amount as agent commission → not real baggage extras
    return null;
  }
  return amount;
}

function normPayToken(v) {
  return String(v || "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_")
    .replace(/\s+/g, "_");
}

function isBankTransferPaymentSlot(payment) {
  if (!payment || typeof payment !== "object") return false;
  const pm = normPayToken(payment.payment_method ?? payment.paymentMethod);
  const pp = normPayToken(payment.payment_provider);
  if (pm === "bank_transfer" || pm === "banktransfer") return true;
  if (
    pp === "bank_transfer" ||
    pp === "banktransfer" ||
    pp === "admin_bank_transfer_finalize"
  ) {
    return true;
  }
  return pm.includes("bank_transfer") || pp.includes("bank_transfer");
}

function hasPendingBankTransferSettlement(order) {
  if (!order || typeof order !== "object") return false;
  const audit =
    order.post_payment_checkout_audit ?? order.postPaymentCheckoutAudit;
  const sd =
    audit && typeof audit === "object"
      ? audit.settlement_decision ?? audit.settlementDecision
      : null;
  if (!sd || typeof sd !== "object") return false;
  const method = String(sd.method || "").trim();
  const remaining = Number(sd.remaining_ils ?? sd.remainingIls);
  return (
    method === "bank_transfer_remaining" &&
    Number.isFinite(remaining) &&
    remaining > 0
  );
}

function paymentIdentityKeys(payment) {
  const keys = new Set();
  const sid = String(
    payment?.payment_session_id ?? payment?.paymentAttemptId ?? ""
  ).trim();
  if (sid) keys.add(`session:${sid}`);
  for (const field of ["payment_id", "Id", "transaction_id", "transactionId"]) {
    const v = String(payment?.[field] ?? "").trim();
    if (v) keys.add(`txn:${v}`);
  }
  const auth = String(
    payment?.AuthNumber ??
      payment?.authNumber ??
      payment?.ACode ??
      payment?.aCode ??
      ""
  ).trim();
  if (auth) keys.add(`auth:${auth}`);
  return keys;
}

function paymentTransId(payment) {
  if (!payment || typeof payment !== "object") return null;
  const src = String(payment.payment_source || payment.source || "").trim();
  const hypAuth = payment.hyp_trans_id || payment.hypTransId || null;
  const captureId =
    payment.capture_trans_id ||
    payment.hyp_capture_trans_id ||
    payment.token_transaction_id ||
    (src === "hyp_j5_soft_capture" ||
    (hypAuth && payment.Id && String(payment.Id) !== String(hypAuth))
      ? payment.Id || payment.payment_id
      : null);
  const tid =
    captureId ||
    hypAuth ||
    payment.TransId ||
    payment.transaction_id ||
    payment.Id ||
    payment.payment_id ||
    null;
  const s = tid != null ? String(tid).trim() : "";
  return s || null;
}

function paymentMethodMeta(payment) {
  if (isBankTransferPaymentSlot(payment)) {
    return { method: "bank_transfer", method_label: "העברה בנקאית" };
  }
  const manual =
    payment?.is_manual_payment_override === true ||
    normPayToken(payment?.payment_provider).includes("manual") ||
    String(payment?.source || "").toLowerCase().includes("manual");
  if (manual) {
    return { method: "manual", method_label: "תשלום ידני" };
  }
  return { method: "credit_card", method_label: "אשראי" };
}

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

/**
 * Installment interest (ריבית תשלומים) on a payment slot — same math as order-page
 * payment links: explicit installment_fee / paymentInterest, else amount − actualAmount
 * when tash > 1. Does not rewrite Hyp capture / sumpaid SSOT.
 * @param {object} payment
 * @returns {number|null}
 */
export function resolveCrmPaymentSlotInterestIls(payment) {
  if (!payment || typeof payment !== "object") return null;
  const tash = Number(
    payment.tash ?? payment.installments ?? payment.numberofpayments
  );
  if (Number.isFinite(tash) && tash <= 1) return null;

  const explicit = num(
    payment.installment_fee ??
      payment.installmentFee ??
      payment.paymentsInterest ??
      payment.paymentInterest ??
      payment.paymentinterest ??
      payment.interestIls
  );
  if (explicit != null && explicit > 0.009) return round2(explicit);

  const amount = num(
    payment.amount ??
      payment.Amount ??
      payment.hyp_gross_amount ??
      payment.hypGrossAmount ??
      payment.total_with_installment_fee
  );
  const actual = num(payment.actualAmount ?? payment.actual_amount);
  if (amount == null || actual == null) return null;
  const delta = round2(amount - actual);
  return delta > 0.009 ? delta : null;
}

/**
 * Customer-facing ILS charged on a captured slot (principal + ריבית when present).
 * Settlement capture stays on actualAmount via resolveLdsPaymentSlotCapturedStrict;
 * CRM display uses Hyp gross (`amount`) when it includes installment fee.
 * @param {object} payment
 * @param {object|null} [order]
 * @param {number} [index]
 * @returns {number}
 */
export function resolveCrmPaymentSlotCustomerGrossIls(
  payment,
  order = null,
  index = 0
) {
  const principal = resolveLdsPaymentSlotCapturedStrict(payment, order, index);
  if (!(principal > 0)) return 0;
  const interest = resolveCrmPaymentSlotInterestIls(payment) || 0;
  const grossField = num(
    payment.amount ??
      payment.Amount ??
      payment.hyp_gross_amount ??
      payment.hypGrossAmount ??
      payment.total_with_installment_fee
  );
  if (grossField != null && grossField > principal + 0.009) {
    return round2(grossField);
  }
  return round2(principal + interest);
}

/**
 * Sum ריבית across captured/paid payment slots (additive for multi-payment packages).
 * @param {object} order
 * @returns {number}
 */
export function sumCrmInstallmentInterestIls(order) {
  const payments = parseLdsOrderPayments(order);
  if (payments.length === 0) {
    const orderLevel = num(order?.paymentinterest ?? order?.paymentInterest);
    return orderLevel != null && orderLevel > 0.009 ? round2(orderLevel) : 0;
  }

  const excludePendingBank = hasPendingBankTransferSettlement(order);
  const seenKeys = new Set();
  let interestSum = 0;

  for (let i = 0; i < payments.length; i++) {
    const payment = payments[i];
    const principal = resolveLdsPaymentSlotCapturedStrict(payment, order, i);
    if (!(principal > 0)) continue;
    if (excludePendingBank && isBankTransferPaymentSlot(payment)) continue;

    const keys = paymentIdentityKeys(payment);
    if (keys.size > 0) {
      let dup = false;
      for (const k of keys) {
        if (seenKeys.has(k)) {
          dup = true;
          break;
        }
      }
      if (dup) continue;
      for (const k of keys) seenKeys.add(k);
    }

    const slotInterest = resolveCrmPaymentSlotInterestIls(payment);
    if (slotInterest) interestSum += slotInterest;
  }

  if (interestSum <= 0.009) {
    const orderLevel = num(order?.paymentinterest ?? order?.paymentInterest);
    if (orderLevel != null && orderLevel > 0.009) return round2(orderLevel);
  }
  return round2(interestSum);
}

/**
 * Received-payment rows for CRM docket — derived only from order.payments[] SSOT
 * (same capture fields as order-page split payment / paid totals).
 * Manual CRM ledger rows (display adjustments) are merged in the docket UI via
 * mergeCrmPaymentsReceived — they never rewrite order.sumpaid / Hyp payments.
 * Amounts are customer-facing gross (כולל ריבית תשלומים when present).
 * @param {object} order
 * @returns {Array<Record<string, unknown>>}
 */
export function buildCrmPaymentsReceivedFromOrder(order) {
  const payments = parseLdsOrderPayments(order);
  if (payments.length === 0) return [];

  const rate = resolveLdsOrderLockedCurrencyRateMap(order);
  const excludePendingBank = hasPendingBankTransferSettlement(order);
  const seenKeys = new Set();
  const rows = [];

  for (let i = 0; i < payments.length; i++) {
    const payment = payments[i];
    const principalIls = resolveLdsPaymentSlotCapturedStrict(payment, order, i);
    if (!(principalIls > 0)) continue;

    if (excludePendingBank && isBankTransferPaymentSlot(payment)) {
      // Pending bank_transfer_remaining — do not list unapproved bank rows as received
      continue;
    }

    const keys = paymentIdentityKeys(payment);
    if (keys.size > 0) {
      let dup = false;
      for (const k of keys) {
        if (seenKeys.has(k)) {
          dup = true;
          break;
        }
      }
      if (dup) continue;
      for (const k of keys) seenKeys.add(k);
    }

    const amountIls = resolveCrmPaymentSlotCustomerGrossIls(payment, order, i);
    const interestIls = resolveCrmPaymentSlotInterestIls(payment);
    const { method, method_label } = paymentMethodMeta(payment);
    const paidAt =
      payment.callback_received_at ||
      payment.created_at ||
      payment.paymentDate ||
      payment.paid_at ||
      null;
    const tash = Number(payment.tash);
    const installments =
      Number.isFinite(tash) && tash > 0 ? tash : null;
    const transactionId = paymentTransId(payment);

    rows.push({
      key: `pay-${i}-${method}-${amountIls}-${transactionId || ""}`,
      index: i,
      method,
      method_label,
      amount_ils: amountIls,
      principal_ils: principalIls,
      interest_ils: interestIls,
      amount_display: fmtIlsUsd(amountIls, null, rate),
      paid_at: paidAt ? String(paidAt) : null,
      installments,
      transaction_id: transactionId,
    });
  }

  return rows;
}

/**
 * Build payment snapshot for CRM docket from live LDS order (order-page SSOT).
 * @param {object} order
 * @returns {Record<string, unknown>}
 */
export function buildCrmLinkedBookingFinancialFromOrder(order) {
  const financial = resolveLdsFinancialState(order, { emitTrace: false });
  const op = ourprice(order);
  const usdMap = resolveLdsOrderAuthoritativeUsdMap(order);
  const rate = resolveLdsOrderLockedCurrencyRateMap(order);
  const showHr = canViewHrCommission();

  // When API row lacks attached SSOT blocks (rare raw fetch), fall back to priceWithHandleFee
  let principalTotal = financial.totalAmount;
  if (principalTotal == null || !(principalTotal > 0)) {
    const withHandle = num(op.priceWithHandleFee);
    const base =
      num(op.priceWithCommissionAndCreditCardFee) ?? num(op.priceWithCommission);
    const agentFeeFallback = resolveAbsoluteAgentHandlingFeeIls(op, {
      columnHandlingFee: order?.handlingfee ?? order?.handling_fee,
    });
    if (withHandle != null && withHandle > 0) principalTotal = withHandle;
    else if (base != null) principalTotal = base + (agentFeeFallback || 0);
  }
  const principalPaid = financial.paidAmount ?? 0;
  // Settlement complete is still principal SSOT (order display_total vs confirmed paid)
  const complete =
    Boolean(financial.isFullyPaid) ||
    (principalTotal != null &&
      principalPaid != null &&
      Number(principalPaid) + 0.02 >= Number(principalTotal));

  // Customer-facing totals include installment interest (ריבית) — order-page amount−actualAmount / installment_fee
  const interestTotal = sumCrmInstallmentInterestIls(order);
  const paymentRows = buildCrmPaymentsReceivedFromOrder(order);
  const grossPaidFromSlots = round2(
    paymentRows.reduce((sum, row) => sum + (Number(row.amount_ils) || 0), 0)
  );

  let total =
    principalTotal != null && interestTotal > 0
      ? round2(Number(principalTotal) + interestTotal)
      : principalTotal;
  let effectivePaid =
    grossPaidFromSlots > 0
      ? grossPaidFromSlots
      : interestTotal > 0
        ? round2(Number(principalPaid || 0) + interestTotal)
        : principalPaid;
  // Fully paid: Hyp gross charged (כולל ריבית) is the authoritative customer total
  if (complete && grossPaidFromSlots > 0.009) {
    total = grossPaidFromSlots;
    effectivePaid = grossPaidFromSlots;
  } else if (
    (effectivePaid == null || effectivePaid <= 0) &&
    total != null &&
    Boolean(complete || order?.paid)
  ) {
    effectivePaid = total;
  }
  // Remaining balance stays against package principal (interest is not order-settlement debt)
  const balance =
    financial.remainingBalance != null
      ? financial.remainingBalance
      : principalTotal != null
        ? Math.max(0, Number(principalTotal) - Number(principalPaid || 0))
        : null;

  const agentFee = resolveAbsoluteAgentHandlingFeeIls(op, {
    columnHandlingFee: order?.handlingfee ?? order?.handling_fee,
  });
  const hrFee = showHr
    ? num(op.hrCommission) ?? num(op?.ourTotalFare?.hrCommission) ?? 0
    : null;
  const supplierNet = showHr
    ? num(order?.api_net_price_ils) ??
      num(op.netPrice) ??
      num(op?.ourTotalFare?.netPrice)
    : null;

  const baggageIls = resolveCrmBaggageIls(order, agentFee);

  const heroLabel = complete ? "סכום סופי ששולם" : "יתרה לתשלום";
  const heroAmount = complete ? total : balance;
  const totalPeer = { peerIls: total, peerUsd: usdMap.totalUsd };
  const heroDisplay = fmtIlsUsd(
    heroAmount,
    complete ? usdMap.totalUsd : null,
    rate,
    complete ? undefined : totalPeer
  );

  return {
    total_due: total,
    total_due_display: fmtIlsUsd(total, usdMap.totalUsd, rate),
    amount_paid: effectivePaid,
    amount_paid_display: fmtIlsUsd(effectivePaid, usdMap.totalUsd, rate),
    balance_due: balance,
    balance_due_display: fmtIlsUsd(balance, null, rate, totalPeer),
    payment_complete: complete,
    hero_amount_label: heroLabel,
    hero_amount: heroAmount,
    hero_amount_display: heroDisplay,
    amount_display: fmtIlsUsd(total, usdMap.totalUsd, rate),
    installment_interest_ils: interestTotal > 0 ? interestTotal : null,
    principal_total_ils: principalTotal,
    fx_rate: rate,
    fx_rate_display: rate != null ? String(rate).replace(/\.0+$/, "") : null,
    currency: String(op.originalCurrency || op.currency || "USD").trim() || "USD",
    amount_usd: usdMap.totalUsd,
    amount_usd_display: usdMap.totalUsd != null ? `$ ${usdMap.totalUsd}` : null,
    baggage_ils: baggageIls,
    baggage_display:
      baggageIls != null && baggageIls > 0 ? fmtIlsUsd(baggageIls, null, rate) : null,
    supplier_net_ils: supplierNet,
    supplier_net_display:
      showHr && supplierNet != null
        ? fmtIlsUsd(supplierNet, usdMap.netUsd, rate)
        : null,
    hr_commission_ils: showHr ? hrFee : null,
    hr_commission_display:
      showHr && hrFee != null && hrFee > 0
        ? fmtIlsUsd(hrFee, usdMap.hrUsd, rate)
        : null,
    agent_commission_ils: agentFee,
    agent_commission_display:
      agentFee != null && Number(agentFee) > 0
        ? fmtIlsUsd(agentFee, usdMap.agentFeeUsd, rate)
        : null,
    status_label: complete
      ? "שולם במלואו"
      : Number(principalPaid) > 0
        ? "יתרה לתשלום"
        : "ממתין לתשלום",
    financial_source: financial.debug?.sourceUsed || financial.debug?.totalSource || null,
  };
}
